import { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { VARIATION_SCHEMAS } from "@/data/variationSchemas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SchemasContext = createContext(null);

/**
 * Deep-merge two schema objects. Overrides fully replace list-like fields
 * (sizes.list, sizes.regions, colours.options, fabrics.options, closures,
 *  header.options, bagType.options, designStyle.options, monogramFont.options).
 * Missing keys fall through to the default.
 */
const mergeSchema = (defaultSchema = {}, override = {}) => {
  const out = { ...defaultSchema };
  for (const key of Object.keys(override)) {
    const ov = override[key];
    if (ov === null || ov === undefined) continue;
    if (Array.isArray(ov)) {
      out[key] = ov;
    } else if (typeof ov === "object") {
      out[key] = { ...(defaultSchema[key] || {}), ...ov };
    } else {
      out[key] = ov;
    }
  }
  return out;
};

export const SchemasProvider = ({ children }) => {
  const [overrides, setOverrides] = useState({});

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/variation-schemas`);
      setOverrides(data || {});
    } catch (e) {
      // Silent — falls back to defaults
    }
  };

  useEffect(() => {
    load();
  }, []);

  const value = useMemo(() => {
    const getSchema = (slug) => mergeSchema(VARIATION_SCHEMAS[slug] || {}, overrides[slug] || {});
    return { overrides, reload: load, getSchema };
  }, [overrides]);

  return <SchemasContext.Provider value={value}>{children}</SchemasContext.Provider>;
};

export const useSchemas = () => useContext(SchemasContext) || {
  overrides: {},
  reload: () => {},
  getSchema: (slug) => VARIATION_SCHEMAS[slug] || {},
};
